import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Users, Trash2, LogOut, Shield, LayoutDashboard, Contact, UserX, History, FileCheck, Bot, User, BookOpen, BotMessageSquare, ShieldAlert, UserCog, Mail, FileText, Settings, RefreshCw, Tags } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AppSidebarProps {
  onClearData: () => void;
  onClearContacts?: () => void;
}

export function AppSidebar({ onClearData, onClearContacts }: AppSidebarProps) {
  const { state } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isEditor, isCliente, isRsvp } = useUserRole();
  const { toast } = useToast();
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showClearContactsDialog, setShowClearContactsDialog] = useState(false);
  
  const isCollapsed = state === "collapsed";
  const isContactsPage = location.pathname === "/contacts";

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logout realizado",
      description: "Até logo!"
    });
    navigate("/login");
  };

  const handleClearData = () => {
    setShowClearDialog(false);
    onClearData();
  };

  const handleClearContacts = () => {
    setShowClearContactsDialog(false);
    if (onClearContacts) {
      onClearContacts();
    }
  };

  return (
    <>
      <Sidebar className={isCollapsed ? "w-14" : "w-60"} collapsible="icon">
        <SidebarTrigger className="m-2 self-end" />

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>
              {!isCollapsed && <span>Navegação</span>}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {(isAdmin || isEditor || isCliente) && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate("/")}
                      className={location.pathname === "/" ? "bg-accent" : ""}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      {!isCollapsed && <span>Dashboard</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {(isAdmin || isEditor || isCliente || isRsvp) && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate("/contacts")}
                      className={location.pathname === "/contacts" ? "bg-accent" : ""}
                    >
                      <Contact className="h-4 w-4" />
                      {!isCollapsed && <span>Contatos</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {(isAdmin || isEditor || isCliente || isRsvp) && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate("/ai-playground")}
                      className={location.pathname === "/ai-playground" ? "bg-accent" : ""}
                    >
                      <Bot className="h-4 w-4" />
                      {!isCollapsed && <span>Assistente de IA</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Email Marketing Section */}
          {(isAdmin || isEditor || isRsvp) && (
            <SidebarGroup>
              <SidebarGroupLabel className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {!isCollapsed && <span>Email Marketing</span>}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {(isAdmin || isEditor) && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigate("/email-forms")}
                        className={location.pathname === "/email-forms" ? "bg-accent" : ""}
                      >
                        <FileText className="h-4 w-4" />
                        {!isCollapsed && <span>Formulários Google</span>}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate("/form-submissions")}
                      className={location.pathname === "/form-submissions" ? "bg-accent" : ""}
                    >
                      <Users className="h-4 w-4" />
                      {!isCollapsed && <span>Inscrições</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {(isAdmin || isEditor) && (
            <SidebarGroup>
              <SidebarGroupLabel className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                {!isCollapsed && <span>Administração</span>}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {isAdmin && (
                    <>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => navigate("/ai-knowledge")}
                          className={location.pathname === "/ai-knowledge" ? "bg-accent" : ""}
                        >
                          <BookOpen className="h-4 w-4" />
                          {!isCollapsed && <span>Base de Conhecimento</span>}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => navigate("/ai-assistant-profile")}
                          className={location.pathname === "/ai-assistant-profile" ? "bg-accent" : ""}
                        >
                          <BotMessageSquare className="h-4 w-4" />
                          {!isCollapsed && <span>Perfil do Assistente</span>}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => navigate("/llm-config")}
                          className={location.pathname === "/llm-config" ? "bg-accent" : ""}
                        >
                          <Settings className="h-4 w-4" />
                          {!isCollapsed && <span>Configurar LLMs</span>}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </>
                  )}
                  {isAdmin && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigate("/form-validation")}
                        className={location.pathname === "/form-validation" ? "bg-accent" : ""}
                      >
                        <FileCheck className="h-4 w-4" />
                        {!isCollapsed && <span>Validar Formulários</span>}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {isAdmin && (
                    <>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => navigate("/user-management")}
                          className={location.pathname === "/user-management" ? "bg-accent" : ""}
                        >
                          <Users className="h-4 w-4" />
                          {!isCollapsed && <span>Gerenciar Usuários</span>}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => navigate("/logs")}
                          className={location.pathname === "/logs" ? "bg-accent" : ""}
                        >
                          <History className="h-4 w-4" />
                          {!isCollapsed && <span>Logs de Auditoria</span>}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => navigate("/contact-access-logs")}
                          className={location.pathname === "/contact-access-logs" ? "bg-accent" : ""}
                        >
                          <ShieldAlert className="h-4 w-4" />
                          {!isCollapsed && <span>Logs de Segurança</span>}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </>
                  )}
                  {isAdmin && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigate("/contact-assignments")}
                        className={location.pathname === "/contact-assignments" ? "bg-accent" : ""}
                      >
                        <UserCog className="h-4 w-4" />
                        {!isCollapsed && <span>Atribuir Contatos</span>}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {isAdmin && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigate("/contact-migration")}
                        className={location.pathname === "/contact-migration" ? "bg-accent" : ""}
                      >
                        <RefreshCw className="h-4 w-4" />
                        {!isCollapsed && <span>Migração de Contatos</span>}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {(isAdmin || isEditor) && (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => navigate("/entity-types")}
                        className={location.pathname === "/entity-types" ? "bg-accent" : ""}
                      >
                        <Tags className="h-4 w-4" />
                        {!isCollapsed && <span>Tipos de Empresa</span>}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {isAdmin && (
                    <>
                      <SidebarMenuItem>
                        <SidebarMenuButton 
                          onClick={() => isContactsPage ? setShowClearContactsDialog(true) : setShowClearDialog(true)}
                        >
                          {isContactsPage ? <UserX className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                          {!isCollapsed && <span>{isContactsPage ? "Limpar Contatos" : "Limpar Dados"}</span>}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          <SidebarGroup>
            <SidebarGroupLabel>
              {!isCollapsed && <span>Sistema</span>}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => navigate("/profile")}
                    className={location.pathname === "/profile" ? "bg-accent" : ""}
                  >
                    <User className="h-4 w-4" />
                    {!isCollapsed && <span>Perfil</span>}
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={handleLogout}>
                    <LogOut className="h-4 w-4" />
                    {!isCollapsed && <span>Sair</span>}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>

      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso irá deletar permanentemente todos os dados
              de restaurantes, operadores e consolidadores do sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearData}>
              Limpar Dados
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showClearContactsDialog} onOpenChange={setShowClearContactsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar todos os contatos?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso irá deletar permanentemente todos os contatos
              cadastrados no sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearContacts}>
              Limpar Contatos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
